import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  TrendingUp,
  Users,
  DollarSign,
  AlertTriangle,
  Banknote,
  Building2,
  Clock,
  Percent,
  PieChart,
  BarChart3,
  LineChart,
  Loader2,
  RefreshCw,
  FileText,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import { fetchAcademicYears, type AcademicYear } from '@/services/academicPlanningService';
import {
  getReportFilterCampuses,
  getRecoveryDashboard,
  getCollectionSummary,
  getOverdueStudents,
  getOperationsReport,
  getTeachingHoursDistribution,
  getFinancialProjections,
  exportRecoveryReport,
  exportOperationsReport,
  exportProjectionsReport,
  exportRecoveryReportPdf,
  exportOperationsReportPdf,
  exportProjectionsReportPdf,
  downloadBlob,
  formatCurrency,
  formatPercentage,
  getAgingBucketColor,
  MONTHS,
  type RecoveryDashboardResponse,
  type CollectionSummaryResponse,
  type OverdueStudentsResponse,
  type OperationsReportResponse,
  type TeachingHoursResponse,
  type FinancialProjectionsResponse,
  type ReportFilters,
  type AgingBucket,
} from '@/services/financialReportingService';

type TabType = 'recovery' | 'operations' | 'projections';
type PeriodType = 'monthly' | 'yearly' | 'custom';

export const ReportingPage: React.FC = () => {
  const { tokens } = useAuthStore();

  // Permissions
  const { hasPermission } = usePermissions();
  const canExportReports = hasPermission(Permission.FINANCE_REPORT_EXPORT);
  const canViewRecovery = hasPermission(Permission.FINANCE_REPORT_RECOVERY);
  const canViewOperations = hasPermission(Permission.FINANCE_REPORT_PAYROLL);
  const canViewProjections = hasPermission(Permission.FINANCE_REPORT_FORECAST);

  // Filter state
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [campuses, setCampuses] = useState<Array<{ id: string; name: string }>>([]);
  // True when the caller is campus-restricted (Financial Manager / Branch
  // Manager) — the dropdown then only lists their campuses.
  const [campusesScoped, setCampusesScoped] = useState(false);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string>('');
  const [selectedCampusId, setSelectedCampusId] = useState<string>('');
  const [periodType, setPeriodType] = useState<PeriodType>('monthly');
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Build available tabs based on permissions
  const availableTabs = React.useMemo(() => {
    const tabs: { id: TabType; label: string; icon: typeof PieChart }[] = [];
    if (canViewRecovery) {
      tabs.push({ id: 'recovery', label: 'Recouvrement', icon: PieChart });
    }
    if (canViewOperations) {
      tabs.push({ id: 'operations', label: 'Operations Financieres', icon: BarChart3 });
    }
    if (canViewProjections) {
      tabs.push({ id: 'projections', label: 'Projections', icon: LineChart });
    }
    return tabs;
  }, [canViewRecovery, canViewOperations, canViewProjections]);

  // Tab state - default to first available tab
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    if (canViewRecovery) return 'recovery';
    if (canViewOperations) return 'operations';
    if (canViewProjections) return 'projections';
    return 'recovery'; // fallback
  });

  // Data state
  const [recoveryData, setRecoveryData] = useState<RecoveryDashboardResponse | null>(null);
  const [collectionData, setCollectionData] = useState<CollectionSummaryResponse | null>(null);
  const [overdueData, setOverdueData] = useState<OverdueStudentsResponse | null>(null);
  const [operationsData, setOperationsData] = useState<OperationsReportResponse | null>(null);
  const [hoursData, setHoursData] = useState<TeachingHoursResponse | null>(null);
  const [projectionsData, setProjectionsData] = useState<FinancialProjectionsResponse | null>(null);

  // Loading states
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load filters (academic years and campuses)
  useEffect(() => {
    const loadFilters = async () => {
      if (!tokens?.accessToken) return;

      try {
        setLoadingFilters(true);
        const [years, campusResp] = await Promise.all([
          fetchAcademicYears(tokens.accessToken),
          getReportFilterCampuses(),
        ]);

        setAcademicYears(years);
        setCampuses(campusResp.campuses || []);
        setCampusesScoped(campusResp.scoped);

        // Set default academic year to current
        const currentYear = years.find(y => y.isCurrent);
        if (currentYear) {
          setSelectedAcademicYearId(currentYear.id);
        } else if (years.length > 0) {
          setSelectedAcademicYearId(years[0].id);
        }
      } catch (err) {
        console.error('Error loading filters:', err);
        setError('Erreur lors du chargement des filtres');
      } finally {
        setLoadingFilters(false);
      }
    };

    loadFilters();
  }, [tokens?.accessToken]);

  // Build filters object
  const buildFilters = useCallback((): ReportFilters | null => {
    if (!selectedAcademicYearId) return null;

    const filters: ReportFilters = {
      academicYearId: selectedAcademicYearId,
      campusId: selectedCampusId || undefined,
      periodType,
    };

    if (periodType === 'monthly') {
      filters.month = selectedMonth;
      filters.year = selectedYear;
    } else if (periodType === 'yearly') {
      filters.year = selectedYear;
    } else if (periodType === 'custom' && startDate && endDate) {
      filters.startDate = startDate;
      filters.endDate = endDate;
    }

    return filters;
  }, [selectedAcademicYearId, selectedCampusId, periodType, selectedMonth, selectedYear, startDate, endDate]);

  // Load data based on active tab
  const loadData = useCallback(async () => {
    const filters = buildFilters();
    if (!filters) return;

    setLoadingData(true);
    setError(null);

    try {
      // Only load data if user has permission for the active tab
      if (activeTab === 'recovery' && canViewRecovery) {
        const [recovery, collection, overdue] = await Promise.all([
          getRecoveryDashboard(filters),
          getCollectionSummary(filters),
          getOverdueStudents(filters),
        ]);
        setRecoveryData(recovery);
        setCollectionData(collection);
        setOverdueData(overdue);
      } else if (activeTab === 'operations' && canViewOperations) {
        const [operations, hours] = await Promise.all([
          getOperationsReport(filters),
          getTeachingHoursDistribution(filters),
        ]);
        setOperationsData(operations);
        setHoursData(hours);
      } else if (activeTab === 'projections' && canViewProjections) {
        const projections = await getFinancialProjections(filters);
        setProjectionsData(projections);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des donnees');
    } finally {
      setLoadingData(false);
    }
  }, [activeTab, buildFilters, canViewRecovery, canViewOperations, canViewProjections]);

  // Load data when filters or tab changes
  useEffect(() => {
    if (selectedAcademicYearId) {
      loadData();
    }
  }, [selectedAcademicYearId, selectedCampusId, periodType, selectedMonth, selectedYear, startDate, endDate, activeTab, loadData]);

  // Excel Export handler
  const handleExportExcel = async () => {
    const filters = buildFilters();
    if (!filters) return;

    setExportingExcel(true);
    try {
      let blob: Blob;
      let filename: string;

      if (activeTab === 'recovery') {
        blob = await exportRecoveryReport(filters);
        filename = `rapport-recouvrement-${new Date().toISOString().split('T')[0]}.xlsx`;
      } else if (activeTab === 'operations') {
        blob = await exportOperationsReport(filters);
        filename = `rapport-operations-${new Date().toISOString().split('T')[0]}.xlsx`;
      } else {
        blob = await exportProjectionsReport(filters);
        filename = `rapport-projections-${new Date().toISOString().split('T')[0]}.xlsx`;
      }

      downloadBlob(blob, filename);
    } catch (err) {
      console.error('Export Excel error:', err);
      setError('Erreur lors de l\'export Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  // PDF Export handler
  const handleExportPdf = async () => {
    const filters = buildFilters();
    if (!filters) return;

    setExportingPdf(true);
    try {
      let blob: Blob;
      let filename: string;

      if (activeTab === 'recovery') {
        blob = await exportRecoveryReportPdf(filters);
        filename = `rapport-recouvrement-${new Date().toISOString().split('T')[0]}.pdf`;
      } else if (activeTab === 'operations') {
        blob = await exportOperationsReportPdf(filters);
        filename = `rapport-operations-${new Date().toISOString().split('T')[0]}.pdf`;
      } else {
        blob = await exportProjectionsReportPdf(filters);
        filename = `rapport-projections-${new Date().toISOString().split('T')[0]}.pdf`;
      }

      downloadBlob(blob, filename);
    } catch (err) {
      console.error('Export PDF error:', err);
      setError('Erreur lors de l\'export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // Use availableTabs (permission-filtered) instead of hardcoded tabs

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  if (loadingFilters) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Show message if user has no report permissions
  if (availableTabs.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reporting Financier</h1>
          <p className="text-gray-500 mt-1">
            Rapports de recouvrement, operations et projections financieres
          </p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
          <p className="text-yellow-700 font-medium">
            Vous n'avez pas les permissions necessaires pour acceder aux rapports financiers.
          </p>
          <p className="text-yellow-600 text-sm mt-2">
            Contactez votre administrateur pour obtenir les permissions requises.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reporting Financier</h1>
          <p className="text-gray-500 mt-1">
            Rapports de recouvrement, operations et projections financieres
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            disabled={loadingData}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={18} className={loadingData ? 'animate-spin' : ''} />
            Actualiser
          </button>
          {canExportReports && (
            <button
              onClick={handleExportExcel}
              disabled={exportingExcel || exportingPdf || loadingData}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {exportingExcel ? <Loader2 size={18} className="animate-spin" /> : <FileSpreadsheet size={18} />}
              Exporter Excel
            </button>
          )}
          {canExportReports && (
            <button
              onClick={handleExportPdf}
              disabled={exportingExcel || exportingPdf || loadingData}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {exportingPdf ? <Loader2 size={18} className="animate-spin" /> : <FileText size={18} />}
              Exporter PDF
            </button>
          )}
        </div>
      </div>

      {/* Global Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* Academic Year */}
          <div className="min-w-[180px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              Annee Academique
            </label>
            <select
              value={selectedAcademicYearId}
              onChange={(e) => setSelectedAcademicYearId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            >
              {academicYears.map(year => (
                <option key={year.id} value={year.id}>
                  {year.name} {year.isCurrent && '(Actuelle)'}
                </option>
              ))}
            </select>
          </div>

          {/* Campus */}
          <div className="min-w-[180px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Building2 size={14} className="inline mr-1" />
              Campus
            </label>
            <select
              value={selectedCampusId}
              onChange={(e) => setSelectedCampusId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="">{campusesScoped ? 'Tous mes campus' : 'Tous les campus'}</option>
              {campuses.map(campus => (
                <option key={campus.id} value={campus.id}>{campus.name}</option>
              ))}
            </select>
          </div>

          {/* Period Type */}
          <div className="min-w-[150px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Clock size={14} className="inline mr-1" />
              Periode
            </label>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as PeriodType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="monthly">Mensuel</option>
              <option value="yearly">Annuel</option>
              <option value="custom">Personnalise</option>
            </select>
          </div>

          {/* Month (for monthly) */}
          {periodType === 'monthly' && (
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Mois</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
              >
                {MONTHS.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Year (for monthly/yearly) */}
          {(periodType === 'monthly' || periodType === 'yearly') && (
            <div className="min-w-[120px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Annee</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}

          {/* Custom date range */}
          {periodType === 'custom' && (
            <>
              <div className="min-w-[150px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date debut</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
              <div className="min-w-[150px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <AlertTriangle className="inline mr-2" size={18} />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {availableTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {loadingData ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              <span className="ml-2 text-gray-500">Chargement des donnees...</span>
            </div>
          ) : (
            <>
              {/* Tab: Recouvrement */}
              {activeTab === 'recovery' && recoveryData && collectionData && overdueData && (
                <RecoveryTab
                  recoveryData={recoveryData}
                  collectionData={collectionData}
                  overdueData={overdueData}
                />
              )}

              {/* Tab: Operations */}
              {activeTab === 'operations' && operationsData && hoursData && (
                <OperationsTab
                  operationsData={operationsData}
                  hoursData={hoursData}
                />
              )}

              {/* Tab: Projections */}
              {activeTab === 'projections' && projectionsData && (
                <ProjectionsTab projectionsData={projectionsData} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== Recovery Tab Component ====================

interface RecoveryTabProps {
  recoveryData: RecoveryDashboardResponse;
  collectionData: CollectionSummaryResponse;
  overdueData: OverdueStudentsResponse;
}

const RecoveryTab: React.FC<RecoveryTabProps> = ({ recoveryData, collectionData, overdueData }) => {
  return (
    <div className="space-y-6">
      {/* KPI Cards — 3 per row so large MAD amounts never overflow the box.
          min-w-0 + break-words keep values contained even when the currency
          formatter joins digits with non-breaking spaces. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white min-w-0">
          <div className="flex items-center gap-2 mb-2 min-w-0">
            <Percent className="h-5 w-5 opacity-80 flex-shrink-0" />
            <span className="text-sm opacity-90">Taux de Recouvrement</span>
          </div>
          <p className="text-3xl font-bold leading-tight tabular-nums">{formatPercentage(recoveryData.globalRecoveryRate)}</p>
        </div>
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white min-w-0">
          <div className="flex items-center gap-2 mb-2 min-w-0">
            <DollarSign className="h-5 w-5 opacity-80 flex-shrink-0" />
            <span className="text-sm opacity-90">Montant Attendu</span>
          </div>
          <p className="text-2xl font-bold leading-tight tabular-nums break-words">{formatCurrency(recoveryData.totalDue)}</p>
          <p className="text-xs opacity-75 mt-1">total à collecter (net des remises)</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white min-w-0">
          <div className="flex items-center gap-2 mb-2 min-w-0">
            <DollarSign className="h-5 w-5 opacity-80 flex-shrink-0" />
            <span className="text-sm opacity-90">Total Collecte</span>
          </div>
          <p className="text-2xl font-bold leading-tight tabular-nums break-words">{formatCurrency(collectionData.totalCollected)}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white min-w-0">
          <div className="flex items-center gap-2 mb-2 min-w-0">
            <DollarSign className="h-5 w-5 opacity-80 flex-shrink-0" />
            <span className="text-sm opacity-90">Reste à Recouvrer</span>
          </div>
          <p className="text-2xl font-bold leading-tight tabular-nums break-words">{formatCurrency(Math.max(0, recoveryData.totalDue - recoveryData.totalPaid))}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white min-w-0">
          <div className="flex items-center gap-2 mb-2 min-w-0">
            <Users className="h-5 w-5 opacity-80 flex-shrink-0" />
            <span className="text-sm opacity-90">Etudiants</span>
          </div>
          <p className="text-2xl font-bold leading-tight tabular-nums">{recoveryData.totalStudents}</p>
          <p className="text-xs opacity-75 mt-1">{recoveryData.paidOnTime} a jour</p>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white min-w-0">
          <div className="flex items-center gap-2 mb-2 min-w-0">
            <AlertTriangle className="h-5 w-5 opacity-80 flex-shrink-0" />
            <span className="text-sm opacity-90">En Retard</span>
          </div>
          <p className="text-2xl font-bold leading-tight tabular-nums">{overdueData.totalOverdue}</p>
          <p className="text-xs opacity-75 mt-1 break-words">{formatCurrency(overdueData.totalAmountDue)}</p>
        </div>
      </div>

      {/* Payment Method Distribution */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 mb-4">Repartition par Mode de Paiement</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {collectionData.distribution.map(dist => (
            <div key={dist.method} className="bg-white rounded-lg p-3 text-center">
              <p className="text-sm text-gray-500">{dist.methodLabel}</p>
              <p className="text-lg font-bold text-gray-900">{formatPercentage(dist.percentage)}</p>
              <p className="text-xs text-gray-400">{formatCurrency(dist.amount)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recovery by Filiere and Level */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Filiere */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
            <h3 className="font-semibold text-white">Taux par Filiere</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {recoveryData.byFiliere.map(item => (
              <div key={item.filiereId} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{item.filiereName}</p>
                  <p className="text-xs text-gray-500">{item.studentCount} etudiants</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary-600">{formatPercentage(item.recoveryRate)}</p>
                  <p className="text-xs text-gray-400">{formatCurrency(item.totalPaid)} / {formatCurrency(item.totalDue)}</p>
                </div>
              </div>
            ))}
            {recoveryData.byFiliere.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-500">Aucune donnee</p>
            )}
          </div>
        </div>

        {/* By Level */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
            <h3 className="font-semibold text-white">Taux par Niveau</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {recoveryData.byLevel.map(item => (
              <div key={item.level} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Niveau {item.level}</p>
                  <p className="text-xs text-gray-500">{item.studentCount} etudiants</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary-600">{formatPercentage(item.recoveryRate)}</p>
                  <p className="text-xs text-gray-400">{formatCurrency(item.totalPaid)} / {formatCurrency(item.totalDue)}</p>
                </div>
              </div>
            ))}
            {recoveryData.byLevel.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-500">Aucune donnee</p>
            )}
          </div>
        </div>
      </div>

      {/* Overdue Students */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-white">
            Etudiants en Retard (apres periode de grace)
          </h3>
          <div className="flex gap-2">
            {(['15-30', '31-60', '61-90', '90+'] as AgingBucket[]).map(bucket => (
              <span
                key={bucket}
                className={`px-2 py-0.5 text-xs rounded-full ${getAgingBucketColor(bucket)}`}
              >
                {bucket}: {overdueData.byBucket[bucket].count}
              </span>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Etudiant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telephone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filiere</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Retard</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant Du</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {overdueData.students.map(student => (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{student.lastName} {student.firstName}</p>
                      <p className="text-xs text-gray-500">{student.studentCode}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{student.email || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{student.phone || '-'}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-900">{student.filiereName}</p>
                    <p className="text-xs text-gray-500">Niveau {student.level}</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAgingBucketColor(student.agingBucket)}`}>
                      {student.daysOverdue} jours
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-600">
                    {formatCurrency(student.amountDue)}
                  </td>
                </tr>
              ))}
              {overdueData.students.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Aucun etudiant en retard
                  </td>
                </tr>
              )}
            </tbody>
            {overdueData.students.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={5} className="px-4 py-3 font-bold text-gray-900">
                    TOTAL ({overdueData.totalOverdue} etudiants)
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-600">
                    {formatCurrency(overdueData.totalAmountDue)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== Operations Tab Component ====================

interface OperationsTabProps {
  operationsData: OperationsReportResponse;
  hoursData: TeachingHoursResponse;
}

const OperationsTab: React.FC<OperationsTabProps> = ({ operationsData, hoursData }) => {
  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Users className="h-5 w-5 opacity-80" />
            <span className="text-sm opacity-90">Personnel Permanent</span>
          </div>
          <p className="text-2xl font-bold">{formatCurrency(operationsData.permanentTotal)}</p>
          <p className="text-xs opacity-75 mt-1">{operationsData.permanentCount} employe(s)</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="h-5 w-5 opacity-80" />
            <span className="text-sm opacity-90">Vacataires</span>
          </div>
          <p className="text-2xl font-bold">{formatCurrency(operationsData.vacataireTotal)}</p>
          <p className="text-xs opacity-75 mt-1">{operationsData.vacataireCount} vacataire(s)</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Banknote className="h-5 w-5 opacity-80" />
            <span className="text-sm opacity-90">Total Masse Salariale</span>
          </div>
          <p className="text-2xl font-bold">{formatCurrency(operationsData.combinedTotal)}</p>
        </div>
      </div>

      {/* Hours Distribution */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 mb-4">Repartition des Heures d'Enseignement</h3>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded-full overflow-hidden flex">
              <div
                className="bg-blue-500 h-full transition-all"
                style={{ width: `${hoursData.permanentPercentage}%` }}
              />
              <div
                className="bg-purple-500 h-full transition-all"
                style={{ width: `${hoursData.vacatairePercentage}%` }}
              />
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
              <span>Permanents: {formatPercentage(hoursData.permanentPercentage)}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded-full" />
              <span>Vacataires: {formatPercentage(hoursData.vacatairePercentage)}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="text-center">
            <p className="text-sm text-gray-500">Total Heures</p>
            <p className="text-xl font-bold text-gray-900">{hoursData.totalHours}h</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Heures Permanents</p>
            <p className="text-xl font-bold text-blue-600">{hoursData.permanentHours}h</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Heures Vacataires</p>
            <p className="text-xl font-bold text-purple-600">{hoursData.vacataireHours}h</p>
          </div>
        </div>
      </div>

      {/* Detail Table - Simplified (no hours column - we have the hours distribution section above) */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
          <h3 className="font-semibold text-white">Detail par Employe</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom & Prenom</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Departement</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Paye</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {operationsData.details.map(employee => (
                <tr key={employee.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{employee.name}</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      employee.type === 'permanent'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {employee.type === 'permanent' ? 'Permanent' : 'Vacataire'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{employee.department || '-'}</td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-green-600">
                    {formatCurrency(employee.netPaid)}
                  </td>
                </tr>
              ))}
              {operationsData.details.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Aucune donnee
                  </td>
                </tr>
              )}
            </tbody>
            {operationsData.details.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={3} className="px-4 py-3 font-bold text-gray-900">TOTAL ({operationsData.details.length} employes)</td>
                  <td className="px-4 py-3 text-right font-bold text-green-600">
                    {formatCurrency(operationsData.combinedTotal)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== Projections Tab Component ====================

interface ProjectionsTabProps {
  projectionsData: FinancialProjectionsResponse;
}

const ProjectionsTab: React.FC<ProjectionsTabProps> = ({ projectionsData }) => {
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  const toggleMonth = (monthKey: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(monthKey)) {
        next.delete(monthKey);
      } else {
        next.add(monthKey);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Banknote className="h-5 w-5 opacity-80" />
            <span className="text-sm opacity-90">Masse Salariale Mensuelle Actuelle</span>
          </div>
          <p className="text-2xl font-bold">{formatCurrency(projectionsData.currentMonthlySalaryMass)}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="h-5 w-5 opacity-80" />
            <span className="text-sm opacity-90">Projection Totale (Periode Selectionnee)</span>
          </div>
          <p className="text-2xl font-bold">{formatCurrency(projectionsData.projectedAnnualCost)}</p>
        </div>
      </div>

      {/* Projections Table with Expandable Month Rows */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
          <h3 className="font-semibold text-white">Projections Financieres</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mois</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Permanents</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Heures Vacataires</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Vacataires</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody>
              {projectionsData.monthlyProjections.map((month, idx) => {
                const monthKey = `${month.year}-${month.month}`;
                const isExpanded = expandedMonths.has(monthKey);
                const hasVacataires = month.vacataireBreakdown && month.vacataireBreakdown.length > 0;

                return (
                  <React.Fragment key={idx}>
                    {/* Main Month Row */}
                    <tr className={`border-b border-gray-200 ${month.isPast ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-2 py-3">
                        {hasVacataires ? (
                          <button
                            onClick={() => toggleMonth(monthKey)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-500" />
                            )}
                          </button>
                        ) : (
                          <span className="w-6 h-6 inline-block"></span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          {month.monthName} {month.year}
                          {month.isPast && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">Paye</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-blue-600">
                        {formatCurrency(month.permanentCost)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">
                        {month.vacataireHours}h
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-purple-600">
                        {formatCurrency(month.vacataireCost)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                        {formatCurrency(month.totalCost)}
                      </td>
                    </tr>

                    {/* Expanded Vacataire Details for this Month */}
                    {isExpanded && hasVacataires && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <div className="bg-purple-50 border-l-4 border-purple-400 mx-2 mb-2 rounded-r">
                            <table className="w-full">
                              <thead className="bg-purple-100/50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-purple-700">Professeur</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-purple-700">Matiere</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-purple-700">Heures</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-purple-700">Taux</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-purple-700">Cout</th>
                                </tr>
                              </thead>
                              <tbody>
                                {month.vacataireBreakdown.map((vac, vIdx) => (
                                  <tr key={vIdx} className="border-t border-purple-100">
                                    <td className="px-4 py-2 text-sm text-gray-900">{vac.name}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600">{vac.subject}</td>
                                    <td className="px-4 py-2 text-right text-sm text-gray-900">{vac.hours}h</td>
                                    <td className="px-4 py-2 text-right text-sm text-gray-900">{formatCurrency(vac.hourlyRate)}/h</td>
                                    <td className="px-4 py-2 text-right text-sm font-semibold text-purple-600">
                                      {formatCurrency(vac.cost)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {projectionsData.monthlyProjections.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Aucune projection disponible
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Total Row - Styled as gradient footer */}
        {projectionsData.monthlyProjections.length > 0 && (
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
            <div className="flex items-center justify-between text-white">
              <span className="font-bold text-lg">TOTAL PREVU</span>
              <div className="flex items-center gap-8">
                <div className="text-right">
                  <div className="text-xs opacity-75">Permanents</div>
                  <div className="font-bold">
                    {formatCurrency(projectionsData.monthlyProjections.reduce((sum, m) => sum + m.permanentCost, 0))}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs opacity-75">Heures</div>
                  <div className="font-bold">
                    {projectionsData.monthlyProjections.reduce((sum, m) => sum + m.vacataireHours, 0).toFixed(1)}h
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs opacity-75">Vacataires</div>
                  <div className="font-bold">
                    {formatCurrency(projectionsData.monthlyProjections.reduce((sum, m) => sum + m.vacataireCost, 0))}
                  </div>
                </div>
                <div className="text-right border-l border-white/30 pl-8">
                  <div className="text-xs opacity-75">Total</div>
                  <div className="font-bold text-xl">
                    {formatCurrency(projectionsData.monthlyProjections.reduce((sum, m) => sum + m.totalCost, 0))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
